import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { ProtectedRoute } from "./components/ProtectedRoute";

import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import CorporateOverview from "./pages/CorporateOverview";
import CorporateRooftops from "./pages/CorporateRooftops";
import CorporateBuyers from "./pages/CorporateBuyers";
import CorporateAcquisitions from "./pages/CorporateAcquisitions";
import CorporateAlerts from "./pages/CorporateAlerts";
import CorporateAuditLog from "./pages/CorporateAuditLog";
import CorporateSettings from "./pages/CorporateSettings";
import RooftopBuyList from "./pages/RooftopBuyList";
import RooftopLiveAuctions from "./pages/RooftopLiveAuctions";
import RooftopTradeQueue from "./pages/RooftopTradeQueue";
import RooftopServiceDrive from "./pages/RooftopServiceDrive";
import RooftopStockPressure from "./pages/RooftopStockPressure";
import RooftopPerformance from "./pages/RooftopPerformance";
import ExtensionPreview from "./pages/ExtensionPreview";
import UploadReport from "./pages/UploadReport";

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen flex items-center justify-center bg-slate-50 text-xs uppercase tracking-wider font-semibold text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "corporate") return <Navigate to="/corporate" replace />;
  if (user.assigned_rooftop_id) return <Navigate to={`/rooftop/${user.assigned_rooftop_id}`} replace />;
  return <Navigate to="/login" replace />;
}

function AppRouter() {
  const location = useLocation();
  // Handle Emergent OAuth callback synchronously
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<RootRedirect />} />

      <Route path="/corporate" element={<ProtectedRoute><CorporateOverview /></ProtectedRoute>} />
      <Route path="/corporate/rooftops" element={<ProtectedRoute><CorporateRooftops /></ProtectedRoute>} />
      <Route path="/corporate/buyers" element={<ProtectedRoute><CorporateBuyers /></ProtectedRoute>} />
      <Route path="/corporate/acquisitions" element={<ProtectedRoute><CorporateAcquisitions /></ProtectedRoute>} />
      <Route path="/corporate/alerts" element={<ProtectedRoute><CorporateAlerts /></ProtectedRoute>} />
      <Route path="/corporate/audit-log" element={<ProtectedRoute><CorporateAuditLog /></ProtectedRoute>} />
      <Route path="/corporate/settings" element={<ProtectedRoute><CorporateSettings /></ProtectedRoute>} />

      <Route path="/rooftop/:rooftop_id" element={<ProtectedRoute><RooftopBuyList /></ProtectedRoute>} />
      <Route path="/rooftop/:rooftop_id/live-auctions" element={<ProtectedRoute><RooftopLiveAuctions /></ProtectedRoute>} />
      <Route path="/rooftop/:rooftop_id/trade-queue" element={<ProtectedRoute><RooftopTradeQueue /></ProtectedRoute>} />
      <Route path="/rooftop/:rooftop_id/service-drive" element={<ProtectedRoute><RooftopServiceDrive /></ProtectedRoute>} />
      <Route path="/rooftop/:rooftop_id/stock-pressure" element={<ProtectedRoute><RooftopStockPressure /></ProtectedRoute>} />
      <Route path="/rooftop/:rooftop_id/performance" element={<ProtectedRoute><RooftopPerformance /></ProtectedRoute>} />

      <Route path="/extension-preview" element={<ProtectedRoute><ExtensionPreview /></ProtectedRoute>} />
      <Route path="/upload" element={<ProtectedRoute><UploadReport /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="bottom-right" richColors theme="light" toastOptions={{ style: { fontFamily: "Montserrat, sans-serif", fontWeight: 500 } }} />
      </BrowserRouter>
    </AuthProvider>
  );
}
